import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import AgentFunctions from "../../../../API/AgentFunctions";
import connectionsHelper from "../../../../API/Connections/connectionsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";

const ConnectionsList = ({ isFunctionsLoading, setDisplayedView }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [isLoading, setIsLoading] = React.useState(true);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = React.useState(null);
  const [displayedConnections, setDisplayedConnections] = React.useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = state?.activeWorkspaceID;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/connections/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, state?.activeWorkspaceID]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.25,
    },
    {
      title: t("settings.list.description"),
      width: 0.25,
    },
    {
      title: t("settings.list.dbType"),
      width: 0.1,
    },
    // {
    //   title: "Authorisation Type",
    // },
    {
      title: t("settings.list.host"),
      width: 0.3,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];
  const loadConnections = async (workspaceID) => {
    try {
      setIsLoading(true);
      const connectionsData = await connectionsHelper.getDatabaseConnections(
        workspaceID,
        null
      );

      let connections = connectionsData?.data?.connectionsByWorkspace?.items;
      connections =
        connections
          ?.map((u) => {
            if (u?._deleted !== true)
              return {
                ...u,
              };
          })
          ?.filter((u) => u != null) || [];
      dispatch("SET_DB_CONNECTIONS", connections || []);

      setDisplayedConnections(connections);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadConnections(state?.activeWorkspaceID);
  }, [state?.activeWorkspaceID]);
  useEffect(() => {
    setDisplayedConnections([...state?.dbConnections]);
  }, [JSON.stringify([...state?.dbConnections])]);

  function identifyOptByID(id) {
    return state.dbConnections.find((u) => u.id === id);
  }
  const handleConnectionsItemSelection = ({ item, index }) => {
    const wsId = state?.activeWorkspaceID;
    if (!wsId || !item?.id) return;
    history.push(`/workspaces/${wsId}/connections/${item?.id}`);
  };
  const handleConnectionItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedConnectionId || selectedItem?.id;
      if (_id) {
        const functionsData =
          await AgentFunctions.getAgentFunctionsByWorkspaceID({
            workspaceID: state?.activeWorkspaceID,
          });
        let functions = functionsData?.data?.functionsByWorkspace?.items;
        functions =
          functions
            ?.map((u) => {
              if (u?.dbConnectionID === _id)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null && u?._deleted != true) || [];
        if (functions.length > 0) {
          AppToasty.show(t("connectionDeletionErrorCaptionDueToConflict"), {
            type: "danger",
          });
          return;
        }
        const result = await connectionsHelper.deleteDatabaseConnection(_id);
        dispatch("SET_DB_CONNECTIONS", [
          ...state?.dbConnections.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("connectionDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedConnectionId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("connectionDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      let skipped = 0;
      for (const id of selectedConnectionIds) {
        const functionsData =
          await AgentFunctions.getAgentFunctionsByWorkspaceID({
            workspaceID: state?.activeWorkspaceID,
          });
        const inUse = (functionsData?.data?.functionsByWorkspace?.items || [])
          .filter((u) => u != null && u?._deleted != true)
          .some((u) => u?.dbConnectionID === id);
        if (inUse) {
          skipped += 1;
          continue;
        }
        await connectionsHelper.deleteDatabaseConnection(id);
      }
      dispatch("SET_DB_CONNECTIONS", [
        ...state?.dbConnections.filter(
          (u) => !selectedConnectionIds.includes(u.id)
        ),
      ]);
      setSelectedConnectionIds([]);
      setShowBulkDeleteChoice(false);
      if (skipped > 0) {
        AppToasty.show(t("connectionDeletionErrorCaptionDueToConflict"), {
          type: "danger",
        });
      } else {
        AppToasty.show(t("connectionDeletionSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("connectionDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = state?.activeWorkspaceID;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/connections/new`);
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteConnectionConfirmation")}
        subCaption={t("settings.list.deleteConnectionConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleConnectionItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);
  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteConnectionConfirmation"),
        })}
        subCaption={t("settings.list.deleteConnectionConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedConnectionIds]);

  return (
    <View
      style={{
        paddingLeft: 10,
        flex: 1,
        width: showSideMenu() ? width - 220 : "100%",
        minWidth: "100%",
        overflow: isLoading && "hidden",
      }}
    >
      {getPlanConfig()?.isDBFunctionsEnabled ? (
        <>
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption={t("settings.list.loadingConnections")}
            />
          ) : (
            <>
              {displayedConnections.length > 0 ? (
                <AppTable
                  isSelectable
                  showPagination
                  itemsPerPage={50}
                  hideSearch={false}
                  searchPlaceholder={t("settings.toolbar.searchConnections")}
                  onSelectionChange={(selectedItems) => {
                    setSelectedConnectionIds(
                      selectedItems?.map((u) => u?.id) || []
                    );
                  }}
                  bulkActions={[
                    {
                      id: "DELETE",
                      label: t("settings.list.delete"),
                      icon: AppAssets.icons.delete,
                      isDestructive: true,
                    },
                  ]}
                  onBulkActionPress={(id) => {
                    if (id === "DELETE" && selectedConnectionIds?.length > 0)
                      setShowBulkDeleteChoice(true);
                  }}
                  actionButtons
                  showDropDownMenuForActions
                  mainActionLabel={t("settings.list.edit")}
                  onClickMainAction={(item, index) => {
                    handleConnectionsItemSelection({
                      id: item?.id,
                      item,
                      index,
                    });
                  }}
                  onActionItemPress={(id, item, index) => {
                    if (id === "EDIT") {
                      handleConnectionsItemSelection({
                        id: item?.id,
                        item,
                        index,
                      });
                    } else if (id === "DELETE") {
                      setSelectedItem(item);
                      setSelectedIndex(index);
                      setSelectedConnectionId(item?.id);
                      setShowChoice(true);
                    }
                  }}
                  actions={[
                    {
                      label: t("settings.list.edit"),
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },
                    {
                      label: t("settings.list.delete"),
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  style={{
                    zIndex: -1,
                    marginTop: 15,
                    height:
                      displayedConnections?.length >= 50
                        ? height - 95
                        : height - 75,
                    paddingRight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedConnections
                    ?.sort((a, b) =>
                      (a?.name || "").localeCompare(b?.name || "")
                    )
                    ?.map((u) => {
                      return {
                        id: u?.id,
                        name: u?.name || " ",
                        description:
                          u?.description ||
                          t("settings.list.noDescriptionProvided"),
                        type: u?.type?.toUpperCase() || " ",
                        host: u?.host,
                        actions: " ",
                      };
                    })}
                >
                  <ToothFairyButton
                    title={t("common.create")}
                    onPress={handleCreation}
                  />
                </AppTable>
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 60,
                    marginHorizontal: "auto",
                    flex: 1,
                    zIndex: -1,
                  }}
                  size={30}
                  icon={AppAssets.icons.data}
                  mainCaption={t("settings.list.noConnectionsFound")}
                  subCaption={t("settings.list.noConnectionsFoundSub")}
                  actionLabel={t("common.create")}
                  onActionPress={handleCreation}
                />
              )}
            </>
          )}
        </>
      ) : (
        <AppPlaceholderView
          accessibilityLabel="db-connections-availability"
          wrapperStyle={{
            maxWidth: 400,
            height: 200,
            marginHorizontal: "auto",
            marginTop: 50,
          }}
          size={30}
          icon={AppAssets.icons.accessDenied}
          mainCaption={t("settings.list.dbConnectionsNotAvailable")}
          subCaption={t("settings.list.upgradeToEnableDBConnections")}
        />
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default ConnectionsList;
